
import { of as observableOf,  Subject ,  Observable, firstValueFrom } from 'rxjs'

import { switchMap,  catchError } from 'rxjs/operators'
import { StatusIdea } from 'interfaces'
import { useState, useReducer, useRef, useEffect, useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { STATUSES_CATEGORIES } from 'routes/find/routes/statuses/statuses.config'
import { StatusIdeasCarousel } from './components/StatusIdeasCarousel'
import { addItems, incrementPage, initialData, resetData, statusesReducer } from './statuses-reducer'
import styles from './QuotesPanel.pcss'
import { useDispatch } from 'react-redux'
import { StoreThunkDispatch } from 'store/state'
import { saveSearch, searchStatusIdeas } from 'services/content/statuses/actions'
import { message } from 'services/snackbar'
import { NavLink } from 'react-router'
import { CuratedFolderListView } from 'components/CuratedFolderView'
import { SuggestedStatusSearches } from './components/SuggestedStatusSearches'
import CollapsibleTextInput from 'components/CollapsibleTextInput'
import EmptyView from 'components/EmptyView'

const categories = STATUSES_CATEGORIES.map(cat => ({ ...cat, name: cat.title }))
const carouselHeight = 284
const EMPTY_STREAM = {} as any // search results are streamless; stable ref keeps CuratedFolderListView memoized

interface StatusIdeasPanelProps {
  title?: string
  className?: string
}

export function StatusIdeasPanel(props: StatusIdeasPanelProps) {
  const intl = useIntl()
  const storeDispatch = useDispatch<StoreThunkDispatch>()
  const [activeCategory, setActiveCategory] = useState(categories[0])
  const [data, dispatch] = useReducer(statusesReducer, initialData)
  const [dataKey, setDataKey] = useState(0)
  const [query, setQuery] = useState('')
  const [searchPage, setSearchPage] = useState(0)
  const [searchResults, setSearchResults] = useState<StatusIdea[]>([])
  const [searchLoading, setSearchLoading] = useState(false)
  const search$ = useRef<Subject<{ query: string, page: number }>>(undefined)
  const title = props.title || intl.formatMessage({ id: 'find.home.sections.status-ideas' })

  useEffect(() => {
    search$.current = new Subject()
    const sub = search$.current.pipe(switchMap(({ query, page }) => {
      if (page === 0) {
        firstValueFrom(storeDispatch(saveSearch(query))).catch(() => {})
      }
      setSearchLoading(true)
      return storeDispatch(searchStatusIdeas(query, page)).pipe(
        catchError(() => {
          return observableOf({ error: true })
        })
      )
    }))
      .subscribe((response: any) => {
        if (response.error) {
          storeDispatch(message(intl.formatMessage({ id: 'errors.generic' }), 'error'))
        } else {
          setSearchResults(current => [...current, ...response.data])
        }
        setSearchLoading(false)
      })

    return () => {
      sub.unsubscribe()
    }
  }, [intl, storeDispatch])

  useEffect(() => {
    if (query) {
      search$.current?.next({ query, page: searchPage })
    }
  }, [query, searchPage])

  const navLink = {
    text: `see all ${query ? '"' + query + '"' : activeCategory.name}`,
    url: `/content/statuses${query ? '?q=' + query : '/' + activeCategory.slug}`
  }

  const onActiveCategoryChange = (id: string) => {
    const category = categories.find(c => c.id === id)
    if (category) {
      dispatch(resetData())
      setDataKey(current => current + 1)
      setActiveCategory(category)
      setQuery('')
      setSearchLoading(false)
    }
  }

  const onStatusesFetched = useCallback((categoryId: string, items: StatusIdea[], page: number) => {
    dispatch(addItems(categoryId, page, items))
  }, [])

  const loadNextPage = useCallback((categoryId: string) => {
    dispatch(incrementPage(categoryId))
  }, [])

  const loadSearchNextPage = useCallback(() => {
    setSearchPage(current => current + 1)
  }, [])

  const onSearch = (val: string) => {
    const q = val.trim()
    if (q) {
      setQuery(q)
      setSearchPage(0)
      setSearchResults([])
      setSearchLoading(true)
    }
  }

  return (
    <section className={`${styles.section} ${props.className || ''}`} data-testid="post-ideas-panel">
      <header className={styles['quotes-header']}>
        <h2 className={`${styles.title} text-ellipsis`}>
          {title}
        </h2>
      </header>
      <SuggestedStatusSearches
        activeQuery={query}
        activeCategoryId={!query ? activeCategory.id : undefined}
        searchElement={(
          <CollapsibleTextInput
            changeOnEnter
            initialActive
            className={styles.searchbox}
            placeholder={intl.formatMessage({ id: 'find.status-ideas.search-hint' })}
            id="post-ideas-panel-search"
            onValueChanged={onSearch}
          />
        )}
        onActiveCategoryChange={onActiveCategoryChange}
        onQueryClick={onSearch}
      />
      <div className={styles.content}>
        <NavLink to={navLink.url} className={styles.link}>{navLink.text}</NavLink>
        {!query && (
          <div style={{ height: `${carouselHeight}px` }}>
            <StatusIdeasCarousel
              key={dataKey}
              category={activeCategory}
              items={data[activeCategory.id]?.items || []}
              page={data[activeCategory.id]?.page || 0}
              onStatusesFetched={onStatusesFetched}
              onLoadMore={loadNextPage}
            />
          </div>
        )}
        {query && (
          <CuratedFolderListView
            stream={EMPTY_STREAM}
            items={searchResults}
            key={query}
            loading={searchLoading}
            onScrollLimit={loadSearchNextPage}
          />
        )}
        {query && !searchLoading && searchResults.length === 0 && (
          <EmptyView
            carousel
            title={<FormattedMessage id="content.msg.status-ideas-search-empty" />}
            subtitle={<FormattedMessage id="content.msg.status-ideas-search-empty-subtitle" />}
            top="30px"
          />
        )}
      </div>
    </section>
  )
}

export default StatusIdeasPanel
